import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, Eye, EyeOff, Star, Shield, Wrench, Users, Handshake } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { HomlyLogo } from '../../components/brand/HomlyLogo';

const API = process.env.REACT_APP_BACKEND_URL;

function GradientOrb({ className }) {
    return (
        <motion.div
            className={`absolute rounded-full blur-[100px] ${className}`}
            animate={{ scale: [1, 1.15, 1], opacity: [0.08, 0.14, 0.08] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
    );
}

export default function ProLoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/professionals/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (res.ok) {
                localStorage.setItem('pro_token', data.access_token);
                localStorage.setItem('pro_profile', JSON.stringify(data.professional));
                navigate('/pro/dashboard');
            } else {
                setError(data.detail || 'Identifiants incorrects');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-background flex relative overflow-hidden">
            {/* Background effects */}
            <GradientOrb className="w-[500px] h-[500px] bg-[#681793] -top-40 -left-40" />
            <GradientOrb className="w-[400px] h-[400px] bg-[#FF914D] bottom-0 right-0" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(104,23,147,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(104,23,147,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

            {/* Left side - Login Form */}
            <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8 relative z-10">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <Link to="/" className="block text-center mb-8">
                        <div className="flex items-center justify-center gap-2">
                            <HomlyLogo size="lg" />
                            <Badge className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white border-0 text-xs font-bold">PRO</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Espace professionnel partenaire</p>
                    </Link>

                    <motion.div
                        className="bg-card backdrop-blur-xl rounded-2xl border border-border p-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h1 className="text-xl font-bold text-foreground mb-1" data-testid="pro-login-title">Connexion</h1>
                        <p className="text-sm text-muted-foreground mb-6">Accédez à votre espace partenaire</p>

                        <form onSubmit={handleLogin} className="space-y-4" data-testid="pro-login-form">
                            {error && (
                                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="pro-login-error">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="text-sm text-muted-foreground mb-1.5 block">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        data-testid="pro-login-email"
                                        type="email"
                                        placeholder="votre@email.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="pl-10"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm text-muted-foreground mb-1.5 block">Mot de passe</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        data-testid="pro-login-password"
                                        type={showPw ? 'text' : 'password'}
                                        placeholder="********"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        className="pl-10 pr-10"
                                        required
                                    />
                                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold h-11"
                                data-testid="pro-login-submit"
                            >
                                {loading ? 'Connexion...' : 'Se connecter'}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </form>

                        <div className="mt-4 text-center space-y-2">
                            <Link to="/pro" className="text-sm text-[#FF914D] hover:underline block">
                                Pas encore inscrit ? Rejoindre Homly Partenaires
                            </Link>
                            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground block">
                                Espace agent immobilier
                            </Link>
                        </div>
                    </motion.div>

                    <div className="mt-6 text-center text-sm text-muted-foreground">
                        <Link to="/cgu" className="hover:text-white transition-colors">CGU</Link>
                        <span className="mx-2">·</span>
                        <Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link>
                        <span className="mx-2">·</span>
                        <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
                    </div>
                </div>
            </div>

            {/* Right side - Visual (desktop only) */}
            <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
                <div className="relative z-10 max-w-md">
                    {/* Floating artisan profile mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <div className="bg-muted backdrop-blur-xl rounded-3xl p-6 border border-border shadow-2xl">
                            <div className="flex items-center gap-3 pb-4 border-b border-border mb-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                                    <Wrench className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <p className="font-bold text-white">Mon espace Pro</p>
                                    <p className="text-xs text-muted-foreground">Tableau de bord partenaire</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { icon: Users, label: 'Recommandations reçues', value: '12' },
                                    { icon: Star, label: 'Note moyenne', value: '4.8/5' },
                                    { icon: Shield, label: 'Profil vérifié', value: 'Oui' }
                                ].map((stat, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-card">
                                        <div className="flex items-center gap-2">
                                            <stat.icon className="w-4 h-4 text-[#FF914D]" />
                                            <span className="text-sm text-muted-foreground">{stat.label}</span>
                                        </div>
                                        <span className="font-bold text-white">{stat.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Floating badge */}
                    <motion.div
                        className="absolute -top-4 -right-4 bg-green-500/90 rounded-xl p-3 shadow-lg border border-white/10"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1, y: [-5, 5, -5] }}
                        transition={{
                            opacity: { duration: 0.5, delay: 0.5 },
                            scale: { duration: 0.5, delay: 0.5 },
                            y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                        }}
                    >
                        <p className="text-white text-sm font-bold flex items-center gap-1.5">
                            <Handshake className="w-4 h-4" /> Nouveau client
                        </p>
                    </motion.div>

                    {/* Quote */}
                    <motion.div
                        className="mt-8 text-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}
                    >
                        <h2 className="text-2xl font-bold text-white mb-3">
                            Recevez des clients
                            <span className="block bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                grâce aux agents immobiliers
                            </span>
                        </h2>
                        <p className="text-muted-foreground">
                            Profil vérifié, avis certifiés et recommandations directes dans une seule plateforme.
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
